// src/components/DemoRequestForm.js
import React, { useState, useEffect } from "react";
import { submitDemoRequest } from "../services/apiService";
import { IoCheckmarkCircle } from 'react-icons/io5';

import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const benefits = [
  "Personalized 1-on-1 walkthrough of the Oolook platform.",
  "Discover how our AI can be tailored to your brand's voice.",
  "Get answers to your specific questions from a product expert.",
  "Explore enterprise features and custom integration options."
];

const DemoRequestForm = () => {
  // ✅ REVERTED STATE AND FIELD NAMES TO camelCase
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    inquiryType: "General Inquiry",
    message: "",
    updates: false
  });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [defaultCountry, setDefaultCountry] = useState('US');

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/country_code/');
        if (!response.ok) throw new Error('Failed to fetch country');
        const countryCode = await response.text();
        if (countryCode && countryCode.length === 2) {
          setDefaultCountry(countryCode);
        }
      } catch (err) {
        console.warn("Could not fetch user country, defaulting to US.", err);
      }
    };
    fetchCountry();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');
    try {
      await submitDemoRequest(formData);
      setStatus('success');
      setFormData({ firstName: "", lastName: "", email: "", company: "", phone: "", inquiryType: "General Inquiry", message: "", updates: false });
    } catch (err) {
      // ✅ GRACEFUL ERROR HANDLING
      setStatus('error');
      if (err.status === 409) {
        setError("A demo request with this email already exists. Our team will be in touch soon!");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-32">
        <IoCheckmarkCircle className="mx-auto h-12 w-12 text-green-400" />
        <h3 className="mt-4 text-lg font-semibold text-dark-text-primary">Thank you!</h3>
        <p className="mt-2 text-dark-text-secondary">Your demo request has been submitted. Our team will be in touch shortly to schedule a time.</p>
      </div>
    );
  }

  return (
    <div className="relative isolate px-6 py-24 sm:py-32 lg:px-8">
      <div className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80" aria-hidden="true">
        <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#9089fc] to-[#ff80b5] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" style={{ clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)' }} />
      </div>
      
      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="max-w-xl lg:max-w-lg">
          <h2 className="text-3xl font-bold tracking-tight text-dark-text-primary">See Oolook in Action</h2>
          <p className="mt-4 text-lg leading-8 text-dark-text-secondary">Schedule a personalized demo to see how our AI-powered platform can revolutionize your social media strategy.</p>
          <ul className="mt-8 space-y-4 text-base leading-7 text-dark-text-secondary">
            {benefits.map(benefit => (
              <li key={benefit} className="flex gap-x-3">
                <IoCheckmarkCircle className="h-7 w-6 flex-none text-dark-accent-start" aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-dark-card/40 backdrop-blur-lg border border-white/10 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl font-bold text-dark-text-primary">Request your demo</h3>
            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                    {/* ✅ REVERTED JSX to use camelCase for `name` and `id` */}
                    <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-dark-text-primary">First name</label>
                        <input type="text" name="firstName" id="firstName" required value={formData.firstName} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                    </div>
                    <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-dark-text-primary">Last name</label>
                        <input type="text" name="lastName" id="lastName" required value={formData.lastName} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                    </div>
                </div>
                <div>
                    <label htmlFor="email" className="block text-sm font-medium text-dark-text-primary">Work Email</label>
                    <input type="email" name="email" id="email" required value={formData.email} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                </div>
                <div>
                    <label htmlFor="company" className="block text-sm font-medium text-dark-text-primary">Company</label>
                    <input type="text" name="company" id="company" required value={formData.company} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 px-3 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start" />
                </div>
                <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-dark-text-primary">Phone number</label>
                    <PhoneInput id="phone" international defaultCountry={defaultCountry} value={formData.phone} onChange={handlePhoneChange} className="mt-2" />
                </div>
                <div>
                    <label htmlFor="inquiryType" className="block text-sm font-medium text-dark-text-primary">Inquiry Type</label>
                    <select id="inquiryType" name="inquiryType" value={formData.inquiryType} onChange={handleChange} className="mt-2 block w-full rounded-md border-0 bg-white/5 py-2.5 pl-3 pr-10 text-dark-text-primary ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start">
                        <option>General Inquiry</option>
                        <option>Enterprise Sales</option>
                        <option>Partnerships</option>
                    </select>
                </div>
                 <div>
                    <label htmlFor="message" className="block text-sm font-medium text-dark-text-primary">How can we help?</label>
                    <div className="mt-2">
                        <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} placeholder="Tell us about your team and your goals..." className="block w-full rounded-md border-0 bg-white/5 py-2 px-3 text-dark-text-primary shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-dark-accent-start"/>
                    </div>
                </div>
                <div className="relative flex items-start">
                    <div className="flex h-6 items-center">
                        <input id="updates" name="updates" type="checkbox" checked={formData.updates} onChange={handleChange} className="h-4 w-4 rounded border-white/10 bg-white/5 text-dark-accent-start focus:ring-dark-accent-start" />
                    </div>
                    <div className="ml-3 text-sm">
                        <label htmlFor="updates" className="text-dark-text-secondary">I'd like to receive product updates and marketing emails.</label>
                    </div>
                </div>
                <div>
                    <button type="submit" disabled={status === 'loading'} className="w-full rounded-md bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50">
                        {status === 'loading' ? 'Submitting...' : 'Schedule My Demo'}
                    </button>
                </div>
                {status === 'error' && <p className="text-sm text-red-400 text-center">{error}</p>}
            </form>
        </div>
      </div>
    </div>
  );
};

export default DemoRequestForm;